import React, { useEffect } from "react";
import { WifiOff, Wifi, AlertTriangle } from "lucide-react";
import { useQzTrayConnection } from "../../../hooks/qztray/useQzTrayConnection";

/**
 * 📶 ConnectionStatusCard
 * Muestra el estado actual del cliente QZ Tray (Conectado, Desconectado o Error)
 */
const ConnectionStatusCard: React.FC = () => {
  const { status, pingConnection, isPingLoading } = useQzTrayConnection();

  // 🚀 Ejecutar una sola prueba de conexión al montar el componente
  useEffect(() => {
    pingConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========================================================
  // 🧩 Determinar estado visual (ícono, color, fondo)
  // ========================================================
  const getStatusInfo = () => {
    switch (status?.status) {
      case "connected":
        return {  
          label: "Conectado",
          color: "text-green-600 dark:text-green-400",
          bg: "bg-green-50 dark:bg-green-900/30",
          icon: <Wifi className="h-5 w-5 text-green-500 dark:text-green-400" />,
        };
      case "error":
        return {
          label: "Error de conexión",
          color: "text-yellow-600 dark:text-yellow-400",
          bg: "bg-yellow-50 dark:bg-yellow-900/30",
          icon: (
            <AlertTriangle className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
          ),
        };
      default:
        return {
          label: "Desconectado",
          color: "text-red-600 dark:text-red-400",
          bg: "bg-red-50 dark:bg-red-900/30",
          icon: <WifiOff className="h-5 w-5 text-red-500 dark:text-red-400" />,
        };
    }
  };

  const { label, color, bg, icon } = getStatusInfo();

  // ========================================================
  // 🎨 Render del componente
  // ========================================================
  return (
    <div className="rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark shadow-md p-5 flex flex-col items-start justify-between transition-colors">
      {/* 🔹 Header con botón de acción */}
      <div className="flex items-center justify-between w-full mb-3">
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Estado Actual
        </h4>

        <button
          onClick={pingConnection}
          disabled={isPingLoading}
          className={`text-xs underline transition-colors ${
            isPingLoading
              ? "text-primary/60 cursor-not-allowed"
              : "text-primary hover:text-primary/80"
          }`}
        >
          {isPingLoading ? "Probando..." : "Reintentar"}
        </button>
      </div>

      {/* 🔸 Estado visual */}
      <div
        className={`flex items-center gap-3 w-full rounded-lg ${bg} px-3 py-2 transition-all`}
      >
        {icon}
        <span className={`text-base font-semibold ${color}`}>{label}</span>
      </div>

      {/* 🔸 Información adicional */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        Última verificación:{" "}
        {status?.checkedAt
          ? new Date(status.checkedAt).toLocaleTimeString()
          : "—"}
      </p>

      {status?.message && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
          {status.message}
        </p>
      )}
    </div>
  );
};

export default ConnectionStatusCard;
